import { useLocation } from 'react-router-dom'
import { useAppStore } from '@/store'
import { useFeatureAccess } from '@/features/featureAccess/useFeatureAccess'
import { isFabricEmbedMode } from '@/lib/fabricEmbedAuth'
import { trackEvent } from '@/utils/analytics'
import HelpAgentLauncher from './HelpAgentLauncher'
import { isHelpAgentExcludedRoute, isHelpAgentHardExcluded } from './routes'

/**
 * Renders the help-agent launcher inside a page header's action cluster.
 *
 * Every 60px header puts one of these at the end of its right-hand side.
 * The app has several header implementations — the shared AppTopbar plus
 * the ones Data, Workflow and Settings roll themselves — so the slot
 * carries the gating rather than each header repeating it, and a header
 * that forgets it simply has no launcher instead of a broken one.
 *
 * It renders nothing unless ALL of these pass:
 *   1. the person may see the help agent at all (`help_agent` feature
 *      access — the per-person `hide_help_agent` flag),
 *   2. an authenticated session,
 *   3. a non-excluded route (login, embeds, auth callbacks, standalone
 *      surfaces), and not Fabric-embed mode,
 *   4. a main (allowlisted) page.
 *
 * It stays rendered while the panel is OPEN — it is the toggle that closes
 * it, and the brand ring is its on-state.
 *
 * The PANEL and the tour are not here: HelpAgentRoot owns those from
 * RootLayout, so a streaming answer survives navigation between headers.
 */
const HelpAgentLauncherSlot = () => {
  const { isEnabled, loaded: featuresLoaded } = useFeatureAccess()
  const userLogged = useAppStore((s) => s.userLogged)
  const helpStage = useAppStore((s) => s.helpStage)
  const openHelpAgent = useAppStore((s) => s.openHelpAgent)
  const closeHelpAgent = useAppStore((s) => s.closeHelpAgent)
  const location = useLocation()

  // Wait for this person's list: until it loads every area reads as on, so a
  // person the help agent is hidden from would see the launcher flash in the
  // header on every page. RootLayout guards the panel the same way (#35143
  // review). The list fails open on error, so this never hides it for good.
  if (!featuresLoaded || !isEnabled('help_agent')) return null
  if (!userLogged) return null
  if (isHelpAgentHardExcluded(location.pathname) || isFabricEmbedMode()) return null
  if (isHelpAgentExcludedRoute(location.pathname)) return null

  // Rendered open OR closed: the avatar is the toggle, and the panel hangs
  // off it. Hiding it while the panel is up would leave the panel with no
  // visible origin, and the control the user just pressed gone.
  const isOpen = helpStage !== 'closed'

  return (
    <HelpAgentLauncher
      isOpen={isOpen}
      onToggle={() => {
        if (isOpen) {
          closeHelpAgent()
          return
        }
        trackEvent('help_agent-opened', { route: location.pathname })
        openHelpAgent()
      }}
    />
  )
}

export default HelpAgentLauncherSlot
